import { patchState, signalStore, withComputed, withHooks, withMethods } from '@ngrx/signals';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';
import { computed, inject } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { exhaustMap, pipe, tap } from 'rxjs';
import { SkillCoreApi } from '@app/core/services/api/skill-core.api';
import { SkillDto } from '@app/core/model/skill-dto';
import { UUID } from '@app/_api/identifyable';
import { setInitialized, withAsyncInitialization } from '@app/pages/home/tree/store/feature/async-initialization.feature';

export const SkillsStore = signalStore(
  { providedIn: 'root' },
  withAsyncInitialization(),
  withEntities<SkillDto>(),
  withComputed(({ entities }) => ({
    $root: computed(() => entities().find(s => !s.parentId)),
    _$parentToChildrenMap: computed(() => Map.groupBy(entities(), s => s.parentId)),
  })),
  withComputed(({ $root, _$parentToChildrenMap }) => ({
    $firstLevelSkills: computed(
      () =>
        _$parentToChildrenMap()
          .get($root()?.id)
          ?.sort((a, b) => a.title.localeCompare(b.title)) ?? [],
    ),
  })),
  withMethods((store, skillCoreApi = inject(SkillCoreApi)) => ({
    reload: rxMethod<void>(
      pipe(
        exhaustMap(() => skillCoreApi.fetchAllSkills()),
        tap(allSkills => patchState(store, setAllEntities(allSkills), setInitialized())),
      ),
    ),

    findChildren(skillId: UUID) {
      return store._$parentToChildrenMap().get(skillId) ?? [];
    },

    findChildrenIds(skillId: UUID) {
      return (
        store
          ._$parentToChildrenMap()
          .get(skillId)
          ?.map(s => s.id) ?? []
      );
    },
  })),
  withHooks({
    onInit(store) {
      store.reload();
    },
  }),
);
